<template>
  <div class="setup-card">
    <div class="setup-card__image">
      <!-- Light image -->
      <img
        :src="setupImageLight"
        alt="My workspace setup - lights on"
        class="setup-image setup-image--light"
        :class="{ 'is-visible': isLightOn }"
      />
      <!-- Dark image -->
      <img
        :src="setupImageDark"
        alt="My workspace setup - lights off"
        class="setup-image setup-image--dark"
        :class="{ 'is-visible': !isLightOn }"
      />
      <div class="setup-card__overlay" :class="{ 'is-dark': !isLightOn }"></div>

      <!-- Light Switch -->
      <div class="light-switch" @click="toggleLight">
        <div class="light-switch__plate">
          <div class="light-switch__toggle" :class="{ 'is-on': isLightOn }">
            <div class="light-switch__knob"></div>
          </div>
        </div>
      </div>

      <!-- Dynamic hotspots -->
      <div
        v-for="(item, index) in setupItems"
        :key="index"
        class="setup-hotspot"
        :style="{ left: item.x + '%', top: item.y + '%' }"
        @mouseenter="activeHotspot = index"
        @mouseleave="activeHotspot = null"
      >
        <div class="hotspot-dot">
          <span class="hotspot-center"></span>
        </div>

        <!-- Tooltip -->
        <div
          class="hotspot-tooltip"
          :class="[
            `hotspot-tooltip--${item.position || 'right'}`,
            { 'is-active': activeHotspot === index },
          ]"
        >
          <div class="tooltip-content">
            <span class="tooltip-name">{{ item.name }}</span>
            <span v-if="item.id" class="tooltip-detail">{{
              $t(`page.about.setupCard.items.${item.id}.detail`)
            }}</span>
            <span v-else-if="item.detail" class="tooltip-detail">{{ item.detail }}</span>
          </div>
          <div class="tooltip-line"></div>
        </div>
      </div>
    </div>

    <!-- Mobile list: devices shown below the image -->
    <div class="setup-card__mobile-list">
      <ul class="mobile-setup-list">
        <li v-for="(item, index) in setupItems" :key="index" class="mobile-setup-item">
          <span class="mobile-setup-item__dot"></span>
          <div class="mobile-setup-item__info">
            <span class="mobile-setup-item__name">{{ item.name }}</span>
            <span class="mobile-setup-item__detail">{{
              item.id ? $t(`page.about.setupCard.items.${item.id}.detail`) : item.detail
            }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useTheme } from '@/composables/useTheme'

// Images
import setupImageLight from '@/assets/images/juan-lias-current-setup.webp'
import setupImageDark from '@/assets/images/juan-lias-current-setup-dark.webp'

const { isDark } = useTheme()

const activeHotspot = ref(null)

// Light state - initially syncs with theme, but can be toggled independently
const isLightOn = ref(true)
const hasUserToggled = ref(false)

// Sync with theme only if user hasn't manually toggled
watch(
  isDark,
  (dark) => {
    if (!hasUserToggled.value) {
      isLightOn.value = !dark
    }
  },
  { immediate: true }
)

// Toggle light manually
const toggleLight = () => {
  hasUserToggled.value = true
  isLightOn.value = !isLightOn.value
}

// Reset user toggle after 30 seconds of inactivity (optional - lets theme sync again)
let resetTimeout = null
watch(isLightOn, () => {
  if (hasUserToggled.value) {
    clearTimeout(resetTimeout)
    resetTimeout = setTimeout(() => {
      hasUserToggled.value = false
    }, 30000)
  }
})

// Setup items configuration - easy to customize
// x, y are percentages from top-left corner
// position: 'left', 'right', 'top', 'bottom' - tooltip direction
const setupItems = ref([
  {
    id: 'dell',
    name: 'DELL G15 5530',
    detail: 'Core i7 13th Gen • GEFORCE RTX • 32GB RAM • 1TB SSD',
    x: 90,
    y: 50,
    position: 'left',
  },
  {
    id: 'mic',
    name: 'Microphone FIFINE K669B',
    detail: 'USB Condenser Mic',
    x: 97.5,
    y: 80,
    position: 'left',
  },
  {
    id: 'webcam',
    name: 'DELL Webcam WB 3023',
    detail: 'Webcam 2k',
    x: 83,
    y: 20,
    position: 'left',
  },
  {
    id: 'mouse',
    name: 'Logitech G502 X Plus',
    detail: 'Mouse',
    x: 72,
    y: 87,
    position: 'right',
  },
  {
    id: 'mousepad',
    name: 'Logitech G640',
    detail: 'Mousepad',
    x: 83,
    y: 94,
    position: 'top',
  },
  {
    id: 'tablet',
    name: 'Wacom CTL-472',
    detail: 'Tablet',
    x: 69,
    y: 79,
    position: 'top',
  },
  {
    id: 'buds1',
    name: 'JBL Wave Bunds',
    detail: 'Wireless Earbuds',
    x: 64,
    y: 72,
    position: 'top',
  },
  {
    id: 'buds2',
    name: 'JBL Tune Bunds',
    detail: 'Wireless Earbuds',
    x: 68,
    y: 72,
    position: 'top',
  },
  {
    id: 'headphones',
    name: 'JBL Tune 720BT',
    detail: 'Over-Ear Headphones',
    x: 53,
    y: 76,
    position: 'top',
  },
  {
    id: 'keyboard',
    name: 'logitech MX Mechanical mini',
    detail: 'Keyboard',
    x: 60,
    y: 86,
    position: 'left',
  },
  {
    id: 'bible',
    name: 'Holy Bible (NIV)',
    detail: 'Spiritual Guidance',
    x: 59,
    y: 70,
    position: 'top',
  },
  {
    id: 'book',
    name: 'Design for a Better World',
    detail: 'UX Book by Don Norman',
    x: 59,
    y: 73.5,
    position: 'top',
  },
  {
    id: 'speaker',
    name: 'Nest mini (2nd Gen)',
    detail: 'Smart Speaker',
    x: 44.2,
    y: 67,
    position: 'right',
  },
  {
    id: 'monitor1',
    name: 'Monitor LG 29" UltraWide',
    detail: 'Full HD • IPS',
    x: 52.5,
    y: 40,
    position: 'bottom',
  },
  {
    id: 'timer',
    name: 'Baseus Countdown Timer',
    detail: 'Pomodoro Timer',
    x: 33,
    y: 75,
    position: 'top',
  },
  {
    id: 'monitor2',
    name: 'Monitor AOC 24"',
    detail: 'Full HD • IPS',
    x: 15,
    y: 44,
    position: 'bottom',
  },
  {
    id: 'controller',
    name: 'Xbox Series X|S Wireless Controller',
    detail: 'For gaming breaks 🎮',
    x: 30,
    y: 81,
    position: 'bottom',
  },
])
</script>

<style scoped>
.setup-card {
  @apply h-full relative flex flex-col;
  min-height: 600px;
}

.setup-card__image {
  @apply relative flex-1;
  min-height: 600px;
}

/* Image styles with crossfade */
.setup-image {
  @apply absolute inset-0 w-full h-full object-cover;
  border-radius: 1rem 1rem 0 0;
  opacity: 0;
  transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.setup-image.is-visible {
  opacity: 1;
}

.setup-image--dark {
  z-index: 1;
}

.setup-image--light {
  z-index: 2;
}

.setup-card__overlay {
  @apply absolute inset-0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(0, 0, 0, 0.2) 30%,
    rgba(0, 0, 0, 0.1) 100%
  );
  border-radius: 1rem 1rem 0 0;
  transition: background 0.8s ease;
  z-index: 3;
}

.setup-card__overlay.is-dark {
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.4) 30%,
    rgba(0, 0, 0, 0.2) 100%
  );
}

/* Light Switch Styles */
.light-switch {
  @apply absolute z-20 flex items-center gap-2 cursor-pointer;
  top: 12px;
  right: 12px;
  padding: 6px 10px;
  background: rgba(30, 30, 30, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.light-switch:hover {
  background: rgba(40, 40, 40, 0.9);
  border-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.light-switch__plate {
  @apply relative;
  width: 36px;
  height: 20px;
  background: #2a2a2a;
  border-radius: 10px;
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.1);
}

.light-switch__toggle {
  @apply absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: linear-gradient(180deg, #555 0%, #333 100%);
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.light-switch__toggle.is-on {
  left: 18px;
  background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%);
  box-shadow:
    0 2px 8px rgba(251, 191, 36, 0.5),
    0 0 20px rgba(251, 191, 36, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.light-switch__knob {
  @apply absolute top-1/2 left-1/2;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
}

/* Hotspot styles */
.setup-hotspot {
  @apply absolute;
  z-index: 10;
  transform: translate(-50%, -50%);
}

.hotspot-dot {
  @apply relative w-5 h-5 cursor-pointer;
}

.hotspot-center {
  @apply absolute top-1/2 left-1/2 w-2.5 h-2.5 rounded-full;
  background: #fff;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.setup-hotspot:hover .hotspot-center {
  @apply w-3 h-3;
  background: var(--color-electric-purple, #8b5cf6);
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.8);
}

/* Tooltip styles */
.hotspot-tooltip {
  @apply absolute pointer-events-none;
  opacity: 0;
  transform: translateY(5px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  z-index: 20;
}

.hotspot-tooltip.is-active {
  opacity: 1;
  transform: translateY(0);
}

/* Tooltip positioning */
.hotspot-tooltip--right {
  left: 100%;
  top: 50%;
  transform: translate(10px, -50%);
  padding-left: 15px;
}

.hotspot-tooltip--right.is-active {
  transform: translate(10px, -50%);
}

.hotspot-tooltip--left {
  right: 100%;
  top: 50%;
  transform: translate(-10px, -50%);
  padding-right: 15px;
}

.hotspot-tooltip--left.is-active {
  transform: translate(-10px, -50%);
}

.hotspot-tooltip--top {
  bottom: 100%;
  left: 50%;
  transform: translate(-50%, -10px);
  padding-bottom: 15px;
}

.hotspot-tooltip--top.is-active {
  transform: translate(-50%, -10px);
}

.hotspot-tooltip--bottom {
  top: 100%;
  left: 50%;
  transform: translate(-50%, 10px);
  padding-top: 15px;
}

.hotspot-tooltip--bottom.is-active {
  transform: translate(-50%, 10px);
}

.tooltip-content {
  @apply flex flex-col px-3 py-2 rounded-lg;
  background: var(--color-deep-black);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tooltip-name {
  @apply text-sm font-semibold text-[var(--color-text-primary)];
}

.tooltip-detail {
  @apply text-xs;
  color: var(--color-text-secondary);
}

/* Tooltip line/connector */
.tooltip-line {
  @apply absolute;
  background: rgba(255, 255, 255, 0.4);
}

.hotspot-tooltip--right .tooltip-line {
  @apply h-px w-3;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.hotspot-tooltip--left .tooltip-line {
  @apply h-px w-3;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.hotspot-tooltip--top .tooltip-line {
  @apply w-px h-3;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.hotspot-tooltip--bottom .tooltip-line {
  @apply w-px h-3;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

/* Card footer */
.setup-card__footer {
  @apply p-4;
  background: var(--color-surface, #1a1a1a);
  border-radius: 0 0 1rem 1rem;
}

.setup-card__header {
  @apply flex items-center gap-2 mb-1;
}

.setup-card__icon {
  @apply text-xl;
}

.setup-card__title {
  @apply text-base font-bold;
  color: var(--color-text-primary, #ffffff);
}

.setup-card__text {
  @apply text-xs;
  color: var(--color-text-secondary, #a0a0a0);
}

/* Responsive */
@media (max-width: 1024px) {
  .setup-card {
    min-height: auto;
  }

  .setup-card__image {
    min-height: 250px;
  }

  .setup-hotspot {
    display: none;
  }

  .light-switch {
    top: 8px;
    right: 8px;
    padding: 4px 8px;
  }

  .light-switch__plate {
    width: 30px;
    height: 16px;
    border-radius: 8px;
  }

  .light-switch__toggle {
    width: 12px;
    height: 12px;
  }

  .light-switch__toggle.is-on {
    left: 16px;
  }
}

/* Mobile setup list */
.setup-card__mobile-list {
  @apply hidden;
}

@media (max-width: 1024px) {
  .setup-card__mobile-list {
    @apply block px-4 pb-4 pt-2;
  }
}

.mobile-setup-list {
  @apply flex flex-col gap-1;
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-setup-item {
  @apply flex items-center gap-3 py-2 px-3 rounded-xl;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  transition: border-color 0.2s ease;
}

.mobile-setup-item:active {
  border-color: var(--color-electric-purple);
}

.mobile-setup-item__dot {
  @apply w-2 h-2 rounded-full flex-shrink-0;
  background: var(--color-electric-purple);
}

.mobile-setup-item__info {
  @apply flex flex-col min-w-0;
}

.mobile-setup-item__name {
  @apply text-sm font-semibold leading-snug truncate;
  color: var(--color-text-primary);
}

.mobile-setup-item__detail {
  @apply text-xs leading-snug;
  color: var(--color-text-secondary);
}
</style>
